export class FormulaSyntaxError extends Error {
    position;
    constructor(message, position) {
        super(message);
        this.position = position;
        this.name = 'FormulaSyntaxError';
    }
}
const OPERATOR_CHARS = new Set(['+', '-', '*', '/', '=', '<', '>', '&', '!']);
export function tokenize(source) {
    const tokens = [];
    let index = 0;
    while (index < source.length) {
        const char = source[index];
        if (/\s/.test(char)) {
            index += 1;
            continue;
        }
        if (char === '{') {
            const end = source.indexOf('}', index);
            if (end < 0)
                throw new FormulaSyntaxError('unterminated column reference', index);
            const body = source.slice(index + 1, end);
            const hash = body.indexOf('#');
            tokens.push(hash < 0
                ? { type: 'column', value: body.trim(), position: index }
                : { type: 'column', value: body.slice(0, hash).trim(), field: body.slice(hash + 1).trim(), position: index });
            index = end + 1;
            continue;
        }
        if (char === '"' || char === "'") {
            let cursor = index + 1;
            let text = '';
            while (cursor < source.length && source[cursor] !== char) {
                // A doubled quote is an escaped quote, the spreadsheet convention monday inherits.
                if (source[cursor] === '\\' && cursor + 1 < source.length) {
                    text += source[cursor + 1];
                    cursor += 2;
                    continue;
                }
                text += source[cursor];
                cursor += 1;
            }
            if (cursor >= source.length)
                throw new FormulaSyntaxError('unterminated string', index);
            tokens.push({ type: 'string', value: text, position: index });
            index = cursor + 1;
            continue;
        }
        if (/[0-9]/.test(char) || (char === '.' && /[0-9]/.test(source[index + 1] ?? ''))) {
            let cursor = index;
            while (cursor < source.length && /[0-9.]/.test(source[cursor]))
                cursor += 1;
            tokens.push({ type: 'number', value: source.slice(index, cursor), position: index });
            index = cursor;
            continue;
        }
        if (/[A-Za-z_]/.test(char)) {
            let cursor = index;
            while (cursor < source.length && /[A-Za-z0-9_]/.test(source[cursor]))
                cursor += 1;
            tokens.push({ type: 'ident', value: source.slice(index, cursor), position: index });
            index = cursor;
            continue;
        }
        if (char === '(' || char === ')') {
            tokens.push({ type: 'paren', value: char, position: index });
            index += 1;
            continue;
        }
        // `,` and `;` are the same token: monday accepts either separator `[for§5]`.
        if (char === ',' || char === ';') {
            tokens.push({ type: 'comma', value: ',', position: index });
            index += 1;
            continue;
        }
        if (OPERATOR_CHARS.has(char)) {
            const two = source.slice(index, index + 2);
            if (two === '>=' || two === '<=' || two === '<>' || two === '!=' || two === '==') {
                tokens.push({ type: 'operator', value: two === '==' ? '=' : two, position: index });
                index += 2;
                continue;
            }
            tokens.push({ type: 'operator', value: char, position: index });
            index += 1;
            continue;
        }
        throw new FormulaSyntaxError(`unexpected character "${char}"`, index);
    }
    return tokens;
}
/** Binding power per operator; higher binds tighter. */
const PRECEDENCE = {
    '=': 1,
    '<>': 1,
    '!=': 1,
    '>': 1,
    '>=': 1,
    '<': 1,
    '<=': 1,
    '&': 2,
    '+': 3,
    '-': 3,
    '*': 4,
    '/': 4,
};
class Parser {
    tokens;
    position = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    peek() {
        return this.tokens[this.position];
    }
    next() {
        const token = this.tokens[this.position];
        if (token === undefined)
            throw new FormulaSyntaxError('unexpected end of formula', -1);
        this.position += 1;
        return token;
    }
    expect(type, value) {
        const token = this.next();
        if (token.type !== type || (value !== undefined && token.value !== value)) {
            throw new FormulaSyntaxError(`expected ${value ?? type}, found "${token.value}"`, token.position);
        }
        return token;
    }
    parse() {
        const node = this.parseExpression(0);
        const rest = this.peek();
        if (rest !== undefined)
            throw new FormulaSyntaxError(`unexpected "${rest.value}"`, rest.position);
        return node;
    }
    parseExpression(minPrecedence) {
        let left = this.parseUnary();
        for (;;) {
            const token = this.peek();
            if (token === undefined || token.type !== 'operator')
                break;
            const precedence = PRECEDENCE[token.value];
            if (precedence === undefined || precedence < minPrecedence)
                break;
            this.next();
            const right = this.parseExpression(precedence + 1);
            left = { type: 'binary', operator: token.value, left, right };
        }
        return left;
    }
    parseUnary() {
        const token = this.peek();
        if (token !== undefined && token.type === 'operator' && (token.value === '-' || token.value === '+')) {
            this.next();
            return { type: 'unary', operator: token.value, operand: this.parseUnary() };
        }
        return this.parsePrimary();
    }
    parsePrimary() {
        const token = this.next();
        if (token.type === 'number')
            return { type: 'number', value: Number(token.value) };
        if (token.type === 'string')
            return { type: 'string', value: token.value };
        if (token.type === 'column') {
            return token.field === undefined
                ? { type: 'column', ref: token.value }
                : { type: 'column', ref: token.value, field: token.field };
        }
        if (token.type === 'paren' && token.value === '(') {
            const inner = this.parseExpression(0);
            this.expect('paren', ')');
            return inner;
        }
        if (token.type === 'ident') {
            const nextToken = this.peek();
            if (nextToken !== undefined && nextToken.type === 'paren' && nextToken.value === '(') {
                this.next();
                const args = [];
                const closing = this.peek();
                if (closing !== undefined && closing.type === 'paren' && closing.value === ')') {
                    this.next();
                }
                else {
                    for (;;) {
                        args.push(this.parseExpression(0));
                        const separator = this.next();
                        if (separator.type === 'comma')
                            continue;
                        if (separator.type === 'paren' && separator.value === ')')
                            break;
                        throw new FormulaSyntaxError(`expected , or ) but found "${separator.value}"`, separator.position);
                    }
                }
                // Function names are case-insensitive `[for§5]`; the registry keys are upper-case.
                return { type: 'call', name: token.value.toUpperCase(), args };
            }
            // A bare identifier is monday's `TRUE` / `FALSE`, else an unquoted string.
            const upper = token.value.toUpperCase();
            if (upper === 'TRUE')
                return { type: 'number', value: 1 };
            if (upper === 'FALSE')
                return { type: 'number', value: 0 };
            return { type: 'string', value: token.value };
        }
        throw new FormulaSyntaxError(`unexpected "${token.value}"`, token.position);
    }
}
/** `null` for an empty expression — two archived columns have exactly that `[for§5]`. */
export function parseFormula(source) {
    if (source === null || source === undefined)
        return null;
    const trimmed = source.trim();
    if (trimmed === '')
        return null;
    return new Parser(tokenize(trimmed)).parse();
}
/** Every `{column}` reference in an expression, in source order, de-duplicated. */
export function referencedColumns(node) {
    const out = [];
    const seen = new Set();
    const walk = (current) => {
        switch (current.type) {
            case 'column': {
                const key = `${current.ref}#${current.field ?? ''}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    out.push(current.field === undefined ? { ref: current.ref } : { ref: current.ref, field: current.field });
                }
                return;
            }
            case 'call':
                for (const arg of current.args)
                    walk(arg);
                return;
            case 'unary':
                walk(current.operand);
                return;
            case 'binary':
                walk(current.left);
                walk(current.right);
                return;
            default:
        }
    };
    if (node !== null)
        walk(node);
    return out;
}
//# sourceMappingURL=parser.js.map